/**
 * 테이블 프리셋 — 시스템의 모든 표 스타일은 여기로 모은다. 인라인 표 스타일 금지.
 *
 * - table        : 표준 데이터 그리드 (재무·HR·결재·메일·채널·관리자 목록)
 * - tableMini    : 카드 위젯·모달 내부의 소형 표 (minWidth 없음)
 * - tableContainer / tableHead / tableBody / tableRowHover
 *                : 검색/제안서 등 외부 자료형 화면의 "상하 경계선 + 가벼운 행" 표 (기존 external 에서 이관)
 *
 * 셀렉터 읽는 법:
 * - "& .MuiTableCell-head"  = 이 표 안의 모든 헤더 셀(th)
 * - "& .MuiTableCell-body"  = 이 표 안의 모든 본문 셀(td)
 * - "& .MuiTableCell-root"  = 헤더+본문 모든 셀
 * 인라인 sx 로 같은 속성을 덮으면 중앙 수정이 안 먹으니(결재 목록에서 실제 사고)
 * 화면에서는 폭(width)·정렬(align)·데이터 색상만 지정한다.
 */
export const createTablePresets = (tokens) => ({
    table: {
        // 표 최소 폭. 컬럼이 많은 그리드가 좁은 화면에서 뭉개지는 대신 가로 스크롤이 생기게 한다.
        // (컨테이너 ds.card 의 overflowX: "auto" 와 세트)
        minWidth: 640,

        // ── 헤더 셀 (th) ──────────────────────────────────────────
        "& .MuiTableCell-head": {
            fontSize: 12,          // 헤더 글자 크기 — 본문(13)보다 1px 작게 해 위계를 만든다
            fontWeight: 700,       // 헤더는 굵게 — 한글은 작은 크기에서 800 이상이면 뭉개지므로 700 상한
            textAlign: "center",   // 한국 ERP 그리드 관행: 헤더는 전부 가운데 정렬
            bgcolor: tokens.colors.bg,    // 옅은 회색 배경으로 헤더 줄을 본문과 구분
            color: tokens.colors.secondary,                       // 헤더 글자색 — 본문보다 연한 회색(보조 정보 톤)
            borderTop: `1px solid ${tokens.colors.border}`,       // 표 상단 경계선
            borderBottom: `1px solid ${tokens.colors.border}`,    // 헤더-본문 구분선 (본문 줄보다 진하게)
            whiteSpace: "nowrap",  // 헤더 라벨이 두 줄로 꺾이지 않게 고정
        },

        // ── 본문 셀 (td) ──────────────────────────────────────────
        "& .MuiTableCell-body": {
            fontSize: 13,                        // 본문 글자 크기 — 국내 업무 그리드 표준(12~13px) 범위
            fontVariantNumeric: "tabular-nums",  // 고정폭 숫자 — 금액 자릿수가 세로로 딱 맞아 비교가 쉬워진다
        },

        // 본문 셀 기본 정렬 = 가운데 (헤더와 같은 한국 ERP 그리드 관행).
        // align 을 명시한 셀(align="right"/"left"/"center")은 그대로 둔다 → 금액만 align="right" 주면 되고,
        // 나머지 컬럼은 아무것도 안 줘도 가운데 정렬. (화면마다 셀에 align="center" 를 반복하지 않게 프리셋에서 처리)
        "& .MuiTableCell-body:not(.MuiTableCell-alignRight):not(.MuiTableCell-alignLeft):not(.MuiTableCell-alignCenter):not(.MuiTableCell-alignJustify)": {
            textAlign: "center",
        },

        // ── 모든 셀 공통 ──────────────────────────────────────────
        "& .MuiTableCell-root": { py: 1.05 },   // 셀 상하 여백(≈8px) — 행 높이를 결정. 줄이면 밀도 ↑

        // 세로 구분선 — 마지막 컬럼은 컨테이너 테두리와 겹치지 않게 제외 (노션풍 풀 그리드의 핵심)
        "& .MuiTableCell-root:not(:last-child)": { borderRight: `1px solid ${tokens.colors.borderLight}` },

        // 본문 행 사이 가로줄 — 헤더 구분선보다 한 단계 연한 색으로 시선 방해를 줄인다
        "& .MuiTableBody-root .MuiTableCell-root": { borderBottom: `1px solid ${tokens.colors.borderLight}` },

        // 행 호버 배경 — 클릭 가능(행 클릭=수정)함을 암시
        "& .MuiTableBody-root .MuiTableRow-root:hover": { bgcolor: tokens.colors.rowHover },
    },

    // ── 외부 자료형 화면(검색/제안서 등) 표 ───────────────────────────
    // 카드형 테두리 대신 "상하 경계선 + 가벼운 행"으로 보이게 하는 세트. (기존 external 프리셋에서 이관)
    // TableContainer(sx=tableContainer) + TableRow 헤더(sx=tableHead) + TableBody(sx=tableBody) + 클릭행(sx=tableRowHover) 조합으로 쓴다.
    tableContainer: {
        borderTop: `1px solid ${tokens.colors.border}`,
        borderBottom: `1px solid ${tokens.colors.border}`,
    },
    tableHead: {
        bgcolor: tokens.colors.borderLight,
        "& th": {
            fontWeight: 700,
            color: tokens.colors.textSecondary,
            borderBottom: `1px solid ${tokens.colors.border}`,
            py: 1.5,
        },
    },
    tableBody: {
        "& td": {
            borderBottom: `1px solid ${tokens.colors.borderLight}`,
            py: 1.35,
        },
    },
    // 클릭 가능한 행 hover. primaryBg 로 선택 가능성을 보여준다.
    tableRowHover: {
        cursor: "pointer",
        "&:hover": { bgcolor: tokens.colors.primaryBg },
    },

    /**
     * 컴팩트 그리드 — 기본 table 과 구조는 같고 밀도만 높다.
     * 글자 1px ↓, 행 높이 ↓, 셀 안의 Typography/Chip/Button 도 같이 줄여
     * 내부 컴포넌트가 행 높이를 키우는 것을 막는다.
     */

    /**
     * 미니 표 — KPI 카드 위젯·모달 안처럼 좁은 컨테이너 전용.
     * minWidth 가 없어서 컨테이너 폭에 그대로 맞고,
     * 세로 구분선 없이 가로줄만 둬서 위젯 안에서 가볍게 보인다.
     */
    tableMini: {
        "& .MuiTableCell-head": {
            fontSize: 11, fontWeight: 700,
            textAlign: "center",
            bgcolor: tokens.colors.bg, color: tokens.colors.secondary,
            borderBottom: `1px solid ${tokens.colors.border}`,   // 헤더 하단선만 (상단선 생략 — 카드 테두리가 대신함)
            whiteSpace: "nowrap",
        },
        "& .MuiTableCell-body": { fontSize: 12, fontVariantNumeric: "tabular-nums" },
        "& .MuiTableCell-root": { py: 0.6, px: 1 },              // 위젯에 맞는 최소 여백
        "& .MuiTableBody-root .MuiTableCell-root": { borderBottom: `1px solid ${tokens.colors.borderLight}` },
    },
});
